// src/pages/MyAccount.tsx
import React, { useState, useEffect } from 'react';
import {
  Spinner,
  Button,
  Modal,
  Form,
  Alert
} from 'react-bootstrap';
import {
  onAuthStateChanged,
  signOut,
  sendPasswordResetEmail,
  User as FirebaseUser,
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/MyAccount.css';
import { logUserAction } from "../utils/logUtils";

interface UserProfile {
  firstName?: string;
  lastName?: string;
  photoURL?: string;
  company?: string;
  role?: string;
  email?: string;
  userId?: string;
  phone?: string;
  websiteLink?: string;
  bootsnippProfile?: string;
  bootplyProfile?: string;
  skills?: string[];
  profession?: string;
}

const getInitials = (firstName: string, lastName: string) => {
  const firstInitial = firstName ? firstName[0].toUpperCase() : '';
  const lastInitial = lastName ? lastName[0].toUpperCase() : '';
  return `${firstInitial}${lastInitial}`;
};

const MyAccount: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [authUser, setAuthUser] = useState<FirebaseUser | null>(null);
  const [formValues, setFormValues] = useState({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    userId: '',
    phone: '',
    websiteLink: '',
    bootsnippProfile: '',
    bootplyProfile: '',
    skills: [] as string[],
    profession: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/login');
        return;
      }

      setAuthUser(user);
      const email = user.email || '';
      setFormValues(prev => ({ ...prev, email }));

      try {
        const docRef = doc(db, 'Users', email);
        const snap = await getDoc(docRef);

        if (snap.exists()) {
          const userData = snap.data();
          const profileData = userData["Email de l'utilisateur"] || userData;
          
          setProfile({
            ...profileData,
            role: userData.role || profileData.role || 'admin',
            userId: profileData.userId || '',
            phone: profileData.phone || '',
            websiteLink: profileData.websiteLink || '',
            bootsnippProfile: profileData.bootsnippProfile || '',
            bootplyProfile: profileData.bootplyProfile || '',
            skills: profileData.skills || [],
            profession: profileData.profession || '',
          });
          setFormValues({
            email,
            firstName: profileData.firstName || '',
            lastName: profileData.lastName || '',
            company: profileData.company || '',
            userId: profileData.userId || '',
            phone: profileData.phone || '',
            websiteLink: profileData.websiteLink || '',
            bootsnippProfile: profileData.bootsnippProfile || '',
            bootplyProfile: profileData.bootplyProfile || '',
            skills: profileData.skills || [],
            profession: profileData.profession || '',
          });
        } else {
          await setDoc(docRef, {
            "Email de l'utilisateur": {
              email,
              firstName: '',
              lastName: '',
              company: '',
              role: 'admin',
              userId: '',
              phone: '',
              websiteLink: '',
              bootsnippProfile: '',
              bootplyProfile: '',
              skills: [],
              profession: '',
            }
          }, { merge: true });
        }
      } catch (err) {
        console.error('Firestore error:', err);
        setError('Erreur lors du chargement du profil. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [navigate]);

  const resetPwd = async () => {
    if (!authUser?.email) return;
    
    try {
      await sendPasswordResetEmail(auth, authUser.email);

      // Enregistrer l'action utilisateur
      const userEmail = authUser.email;
      await logUserAction(
        userEmail,
        "Réinitialisation mot de passe",
        `Demande de réinitialisation pour ${userEmail}`
      );

      alert('Un email de réinitialisation a été envoyé !');
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Erreur lors de la réinitialisation du mot de passe');
    }
  };

  const handleSave = async () => {
    if (!authUser?.email) return;

    try {
      const docRef = doc(db, 'Users', authUser.email);
      
      // Récupérer les données existantes
      const existingDoc = await getDoc(docRef);
      let existingData = {};
      
      if (existingDoc.exists()) {
        const userData = existingDoc.data();
        existingData = userData["Email de l'utilisateur"] || userData;
      }

      // Préparer les données à sauvegarder selon le rôle
      let dataToSave: any = {
        email: authUser.email,
        role: profile?.role || 'admin'
      };

      if (profile?.role === 'admin') {
        // Pour les admins, sauvegarder tous les champs
        dataToSave = {
          ...existingData,
          ...formValues,
          email: authUser.email,
          role: profile?.role || 'admin'
        };
      } else {
        // Pour les autres rôles (foodinnov, etc.), sauvegarder seulement profession et company
        dataToSave = {
          ...existingData,
          profession: formValues.profession,
          company: formValues.company,
          email: authUser.email,
          role: profile?.role || 'admin'
        };
      }

      await setDoc(
        docRef,
        { 
          "Email de l'utilisateur": dataToSave
        },
        { merge: true }
      );

      // Enregistrer l'action utilisateur
      const userEmail = authUser.email;
      await logUserAction(
        userEmail,
        "Mise à jour profil",
        `Mise à jour du profil de ${userEmail}`
      );

      // Mettre à jour l'état local
      setProfile({
        ...profile,
        profession: formValues.profession,
        company: formValues.company,
        ...(profile?.role === 'admin' && {
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          userId: formValues.userId,
          phone: formValues.phone,
          websiteLink: formValues.websiteLink,
          bootsnippProfile: formValues.bootsnippProfile,
          bootplyProfile: formValues.bootplyProfile,
          skills: formValues.skills,
        })
      });

      setShowModal(false);
    } catch (err) {
      console.error('Update error:', err);
      setError('Erreur lors de la mise à jour du profil');
    }
  };

  if (loading) {
    return (
      <div className="MyAccount-container">
        <Spinner animation="border" />
        <span className="ms-2">Chargement de votre profil...</span>
      </div>
    );
  }

  if (error || !authUser) {
    return (
      <>
        <div className="MyAccount-container">
          <div className="MyAccount-card">
            <Alert variant="danger">
              <Alert.Heading>Erreur</Alert.Heading>
              <p>{error || 'Impossible de charger votre profil'}</p>
              <div className="d-flex justify-content-between">
                <Button onClick={handleLogout}>Se déconnecter</Button>
                <Button variant="secondary" onClick={() => window.location.reload()}>
                  Réessayer
                </Button>
              </div>
            </Alert>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const { email, firstName, lastName, company } = formValues;
  const registrationDate = new Date(authUser.metadata.creationTime || '').toLocaleDateString();
  const lastSignInTime = new Date(authUser.metadata.lastSignInTime || '').toLocaleDateString();

  return (
    <>
      <Header 
        isAuthenticated={true} 
        onLoginClick={() => navigate('/login')} 
        onHomeClick={() => navigate('/')} 
        onLogoutClick={handleLogout}
      />
      <div className="profile-page-wrapper-modern">
        <div className="profile-card-modern">
          <div className="profile-header-modern">
            <div className="profile-avatar-container-modern">
              <div className="profile-avatar-circle-modern">
                {getInitials(firstName, lastName)}
              </div>
            </div>
            <div className="profile-name-title-modern">
              <h2 className="profile-name-modern">{firstName} {lastName}</h2>
              <p className="profile-title-modern">{profile?.profession}</p>
            </div>
            <div className="profile-actions-modern">
              <button className="profile-action-button-modern" onClick={() => setShowModal(true)}>Editer Profile</button>
              <button className="profile-action-button-modern" onClick={resetPwd}>Changer Mot de Passe</button>
            </div>
          </div>

          <div className="profile-details-section-modern">
            <h3 className="section-heading-modern">Informations du Compte</h3>
            <div className="details-grid-modern">
              <div className="detail-item-modern">
                <span className="detail-label-modern">Email</span>
                <span className="detail-value-modern">{authUser.email}</span>
              </div>
              <div className="detail-item-modern">
                <span className="detail-label-modern">Prénom</span>
                <span className="detail-value-modern">{firstName || 'Non renseigné'}</span>
              </div>
              <div className="detail-item-modern">
                <span className="detail-label-modern">Nom</span>
                <span className="detail-value-modern">{lastName || 'Non renseigné'}</span>
              </div>
              <div className="detail-item-modern">
                <span className="detail-label-modern">Profession</span>
                <span className="detail-value-modern">{formValues.profession || profile?.profession || 'Non renseignée'}</span>
              </div>
              <div className="detail-item-modern">
                <span className="detail-label-modern">Société</span>
                <span className="detail-value-modern">{formValues.company || 'Non renseignée'}</span>
              </div>
              {profile?.role === 'admin' && (
                <div className="detail-item-modern">
                  <span className="detail-label-modern">Rôle</span>
                  <span className="detail-value-modern">{profile?.role || 'Non renseigné'}</span>
                </div>
              )}
              <div className="detail-item-modern">
                <span className="detail-label-modern">Compte créé le</span>
                <span className="detail-value-modern">{registrationDate}</span>
              </div>
              <div className="detail-item-modern">
                <span className="detail-label-modern">Dernière connexion le</span>
                <span className="detail-value-modern">{lastSignInTime}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

             <Modal 
         show={showModal} 
         onHide={() => setShowModal(false)}
         centered
         size="lg"
         style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
       >
         <Modal.Header closeButton>
           <Modal.Title>Modifier le profil</Modal.Title>
         </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Champs en lecture seule pour tous les utilisateurs */}
            <Form.Group className="mb-3">
              <Form.Label>Prénom</Form.Label>
              <Form.Control
                type="text"
                value={formValues.firstName}
                readOnly
                style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Nom</Form.Label>
              <Form.Control
                type="text"
                value={formValues.lastName}
                readOnly
                style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
              />
            </Form.Group>
                         <Form.Group className="mb-3">
               <Form.Label>Email</Form.Label>
               <Form.Control
                 type="email"
                 value={authUser.email || ''}
                 readOnly
                 style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
               />
             </Form.Group>
            
            {/* Champs modifiables pour tous les utilisateurs */}
            <Form.Group className="mb-3">
              <Form.Label>Profession</Form.Label>
              <Form.Control
                type="text"
                value={formValues.profession}
                onChange={(e) => setFormValues({ ...formValues, profession: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Société</Form.Label>
              <Form.Control
                type="text"
                value={formValues.company}
                onChange={(e) => setFormValues({ ...formValues, company: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Enregistrer
          </Button>
        </Modal.Footer>
      </Modal>
      <Footer />
    </>
  );
};

export default MyAccount;